import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import usePlans from '../hooks/usePlans';
import PlanCards from '../components/PlanCards';
import { Zap, ArrowRight, ChevronDown, ChevronUp, X, Menu, Loader2 } from 'lucide-react';
// X is used for the mobile nav close button

const FAQS = [
  { q: 'What are credits?', a: 'Credits pay for finding prospects and verifying their email addresses. Each plan includes a monthly allowance (see the plan cards above). Extra credits can be bought inside the app and never expire.' },
  { q: 'Do I need my own email or SMS account?', a: 'Yes — for email you\'ll connect your own SMTP server or Resend API key, and for SMS you\'ll connect your own Twilio account. This means your sending domain and phone number are fully yours, giving you complete control over your deliverability and sending limits.' },
  { q: 'Do unused credits carry over?', a: 'Your monthly credit allowance resets each billing period. Top-up credits (purchased separately) never expire and always carry over.' },
  { q: 'What payment methods are accepted?', a: 'All major credit and debit cards, plus PayPal — processed securely through ThriveCart.' },
  { q: 'Can I cancel anytime?', a: 'Yes — cancel at any time from inside the app. You retain access until the end of your current billing period. No lock-in, no cancellation fees.' },
  { q: 'Do you offer annual billing?', a: 'Yes - pay annually and get 2 months free. You still receive your credits every month.' },
];

const PricingPage = () => {
  const { isAuthenticated } = useAuth();
  const { hash } = useLocation();
  const [billing, setBilling] = useState('monthly');

  useEffect(() => {
    if (hash === '#plan') {
      // Small delay ensures the element is painted before scrolling
      const timer = setTimeout(() => {
        const el = document.getElementById('plan');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [hash]);

  const [openFaq, setOpenFaq] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const { plans, topups, loading: plansLoading, error: plansError, reload } = usePlans();
  const scrollToPlans = () => document.getElementById('plan')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleGetPlan = async (plan, billingCycle) => {
    const planName = plan.name;
    const key = `${planName}-${billingCycle}`;
    setCheckoutError('');
    if (isAuthenticated && !plan.id) {
      setCheckoutError('This plan is not available yet - please contact support.');
      return;
    }
    setCheckoutLoading(key);
    try {
      if (isAuthenticated) {
        // Logged-in user: use the authenticated checkout endpoint
        const checkoutRes = await api.post('/billing/checkout', {
          plan_id: plan.id,
          billing_cycle: billingCycle,
        });
        if (checkoutRes.data.checkout_url) {
          window.location.href = checkoutRes.data.checkout_url;
        } else {
          throw new Error(checkoutRes.data.error || 'No checkout URL');
        }
      } else {
        // Unauthenticated visitor: public checkout — no account needed
        const res = await api.get('/billing/public-checkout', {
          params: { plan: planName.toLowerCase(), cycle: billingCycle },
        });
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        } else {
          throw new Error(res.data.error || 'No checkout URL');
        }
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError('Could not start checkout. Please try again or contact support.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shadow-indigo-200">
                <Zap size={16} className="text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 tracking-tight">Outreach Engine</span>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard" className="bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors">
                  Go to App
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Sign In</Link>
                  <button onClick={scrollToPlans} className="bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                    Get Started
                  </button>
                </>
              )}
            </div>
            <button className="md:hidden p-2 text-gray-600" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            <Link to="/" className="block text-sm font-medium text-gray-700 py-2">Home</Link>
            <Link to="/login" className="block text-sm font-medium text-gray-700 py-2">Sign In</Link>
            <button onClick={scrollToPlans} className="block w-full text-center bg-indigo-600 text-white text-sm font-semibold px-5 py-3 rounded-lg">Get Started</button>
          </div>
        )}
      </header>

      {/* HEADER */}
      <section className="pt-16 pb-12 px-6 bg-gradient-to-b from-indigo-50/60 to-white text-center">
        <p className="text-indigo-600 text-xs font-bold uppercase tracking-widest mb-3">Pricing</p>
        <h1 className="text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
          Simple, transparent pricing
        </h1>
        <p className="text-lg text-gray-500 max-w-xl mx-auto mb-10">
          Pick the monthly credit allowance that fits. Every plan includes every feature. No per-seat fees.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex items-center bg-gray-200 rounded-xl p-1 gap-1 shadow-inner">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-7 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
              billing === 'monthly'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-300'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`flex items-center gap-2 px-7 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
              billing === 'yearly'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-300'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Yearly
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              billing === 'yearly' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'
            }`}>
              Save 2 months
            </span>
          </button>
        </div>
      </section>

      {/* PLANS */}
      <section id="plan" className="pb-20 px-6">
        {plansLoading ? (
          <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-indigo-600" /></div>
        ) : plansError ? (
          <div className="max-w-md mx-auto text-center py-10">
            <p className="text-gray-600 mb-4">We couldn't load the plans right now.</p>
            <button onClick={reload} className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Try again</button>
          </div>
        ) : (
          <PlanCards
            plans={plans}
            billing={billing}
            onSelect={handleGetPlan}
            loadingKey={checkoutLoading}
          />
        )}
        {checkoutError && (
          <div className="max-w-md mx-auto mt-6 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">{checkoutError}</div>
        )}
        <p className="text-center text-gray-500 text-xs mt-6">Cancel anytime · No setup fees</p>
      </section>

      {/* CREDIT TOP-UPS */}
      {topups.length > 0 && (
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-indigo-600 text-xs font-bold uppercase tracking-widest mb-2">Add-ons</p>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Need more credits?</h2>
              <p className="text-gray-500">Every plan includes monthly credits. Buy more any time - top-up credits never expire.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {topups.map(({ key, credits, price, per_credit, best }) => (
                <div key={key} className={`relative rounded-2xl p-6 text-center transition-all hover:-translate-y-1 ${
                  best ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-200' : 'bg-white border border-gray-200 shadow-sm'
                }`}>
                  {best && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Most popular
                    </div>
                  )}
                  <p className={`text-base font-bold mb-1.5 ${best ? 'text-white' : 'text-gray-900'}`}>{`${Number(credits).toLocaleString()} credits`}</p>
                  <p className={`text-4xl font-extrabold mb-1 ${best ? 'text-white' : 'text-indigo-600'}`}>{`$${price}`}</p>
                  <p className={`text-xs ${best ? 'text-indigo-200' : 'text-gray-400'}`}>{`$${Number(per_credit).toFixed(3)} each`}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-400 mt-6">
              Top-ups are available from inside the app after subscribing.
            </p>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-indigo-600 text-xs font-bold uppercase tracking-widest mb-2">FAQ</p>
            <h2 className="text-3xl font-extrabold text-gray-900">Frequently asked questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map(({ q, a }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <button
                  className="w-full flex items-center justify-between px-7 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{q}</span>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                    openFaq === i ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    {openFaq === i
                      ? <ChevronUp size={14} className="text-indigo-600" />
                      : <ChevronDown size={14} className="text-gray-500" />}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-7 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-100">
                    <div className="pt-4">{a}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-indigo-600 to-violet-700">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4 tracking-tight">
            Ready to start? Join today.
          </h2>
          <p className="text-indigo-200 mb-8 text-lg">
            Cancel anytime. No setup fees.
          </p>
          <button
            onClick={scrollToPlans}
            className="inline-flex items-center gap-2 bg-white text-indigo-600 font-bold text-base px-10 py-4 rounded-xl hover:bg-indigo-50 transition-all shadow-2xl hover:-translate-y-0.5"
          >
            Get Started
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-950 border-t border-gray-800 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Zap size={14} className="text-white" />
            </div>
            <span className="text-white font-bold text-sm">Outreach Engine</span>
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Home</Link>
            <Link to="/login" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Sign In</Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Terms</Link>
            <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Privacy</Link>
          </div>
          <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Outreach Engine</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
